import React from "react";
import styled from "styled-components";
import slugify from "slugify";
import { Link } from "react-router-dom";
import {
  COLOR_BLACK,
  COLOR_BLUE,
  COLOR_GREY_CITY,
  COLOR_WHITE,
} from "../constants/colors";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import bedroomIcon from "../assets/bedroom_icon.svg";
import roomIcon from "../assets/room_icon.svg";
import surfaceIcon from "../assets/surface_icon.svg";
import { IAnnonceInterface } from "../interfaces/annonce";
import logo from "../assets/logo.svg";

const Wrapper = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 416px;
  min-height: 476px;
  box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.05);
  border: solid 2px rgba(168, 168, 168, 0.24);
  margin-left: auto;
  margin-right: auto;
  border-radius: 10px;
  background-color: ${COLOR_WHITE};
  font-size: 26px;
  text-decoration: none;
  color: ${COLOR_BLACK};

  .img {
    width: 100%;
    height: 190px;
    border-radius: 10px 10px 0 0;
    margin: 0 0 31px 0;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    &.logo {
      background-size: 40%;
    }
  }

  .separation {
    width: 100%;
    margin-top: 38px;
    border: 1px solid rgba(112, 112, 112, 0.11);
  }

  :hover {
    cursor: pointer;
  }

  .visible-false {
    visibility: hidden;
  }

  @media screen and (max-width: 1300px) {
    width: 380px;
  }
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  width: 323px;

  > .text {
    min-height: 70px;
    margin: 0 0 13px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.31;
    letter-spacing: normal;
    text-align: left;
  }
`;

const PriceLocation = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  vertical-align: middle;

  .price {
    color: ${COLOR_BLUE};
    font-weight: bold;
    white-space: nowrap;
    margin-right: 8px;
  }

  .location {
    color: ${COLOR_GREY_CITY};
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 1;

    .text {
      margin: 0 31px 0 7px;
      color: ${COLOR_GREY_CITY};
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 220px;
    }

    .icon {
      font-size: 26px;
    }
  }
`;

interface GeneralInfosInterface {
  nbCol: number;
}

const GeneralInfos = styled.div<GeneralInfosInterface>`
  display: grid;
  grid-template-columns: repeat(${(props) => props.nbCol}, 0.5fr);
  grid-gap: 10px;
  font-size: 18px;
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 20px;

  .info {
    display: flex;
    width: auto;
    align-items: center;
  }

  /*.info:first-child {
    margin-right: 34px;
    margin-left: 40px;
  }

  .info:last-child{
    margin-left: 34px;
    margin-right: 40px;
  }*/

  .icon-bubble {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 14px 0 0;
    width: 44px;
    height: 44px;
    border-radius: 7px;
    background-color: #e0e0e0;
  }

  img {
    width: 22px;
    margin: 0 auto;
  }

  ::-webkit-scrollbar {
    display: none;
  }

  /*@media screen and (max-width: 1300px ) {
    width: 350px;
    grid-template-columns: repeat(2, 1fr);
  }*/
`;

const Disponible = styled.div`
  font-size: 14px;
  align-self: flex-start;
  position: relative;
  top: 16px;
`;

const Card = ({ annonce }: { annonce: IAnnonceInterface }) => {
  const annonceId = annonce.id + ""
  const annonceDescription =
    annonce.type +
    " " +
    (annonce.type !== "Parking"
      ? annonce.rooms +
        (annonce.rooms > 2 ? " pièces de " : " pièce de ") +
        annonce.surface +
        "m²"
      : "") +
    " à " +
    annonce.city;

  const bottomBar =
    annonce?.rooms !== "null" ||
    annonce.bedrooms !== "null" ||
    annonce?.surface !== null;

  return (
    <Wrapper to={`/location-vente/${annonceId}`}>
      <div
        className={annonce.mainImage === logo ? "img logo" : "img"}
        style={{ backgroundImage: `url(${annonce.mainImage}` }}
      ></div>
      <Description>
        <p className="text">{annonceDescription}</p>
        <PriceLocation>
          <div className="price">{annonce.price} €</div>
          <div className="location">
            <LocationOnOutlinedIcon className="icon" />
            <span className="text">{annonce.city}</span>
          </div>
        </PriceLocation>
        {annonce.dateRendered !== null && (
          <Disponible>Disponible le {annonce.dateRendered}</Disponible>
        )}
      </Description>
      {bottomBar && (
        <>
          <div className="separation"></div>
          <GeneralInfos nbCol={annonce.consumption ? 4 : 3}>
            {annonce?.surface !== null && (
              <div className="info">
                <span className="icon-bubble">
                  <img src={surfaceIcon} alt="surface" />
                </span>
                {annonce.surface}m²
              </div>
            )}
            {annonce?.rooms !== "null" && (
              <div className="info">
                <span className="icon-bubble">
                  <img src={roomIcon} alt="room" />
                </span>
                {annonce.rooms}
              </div>
            )}
            {annonce.bedrooms !== "null" && (
              <div className="info">
                <span className="icon-bubble">
                  <img src={bedroomIcon} alt="bedroom" />
                </span>
                {annonce.bedrooms}
              </div>
            )}
            {annonce.consumption && (
              <div className="info">
                <span className="icon-bubble">DPE</span>
                {annonce.consumption}
              </div>
            )}
          </GeneralInfos>
        </>
      )}
      {!bottomBar && (
        <>
          <div className="separation visible-false"></div>
          <GeneralInfos nbCol={4} className="visible-false">
            <div className="info">
              <span className="icon-bubble">DPE</span>
            </div>
          </GeneralInfos>
        </>
      )}
    </Wrapper>
  );
};

export default Card;
